import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and a
 * JS-measured height for smooth expand/collapse animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on the inert attribute to manage focus/interactivity for the collapsed
 * content, and measures the content's natural height via scrollHeight just before it opens,
 * writing that value to a CSS custom property (--disclosure-height) so the stylesheet can
 * transition `height` smoothly between 0 and that measured value. This avoids relying on
 * CSS grid-template-rows auto-sizing (0fr/1fr), which does not reliably resolve to the
 * content's real height in some browsers/layout contexts.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   *
   * Before opening, measures the content's natural height (via scrollHeight, which reflects
   * the content's real size even while it's visually collapsed via height:0/overflow:hidden)
   * and writes it to the --disclosure-height custom property. The stylesheet then transitions
   * `height` from 0 to that measured pixel value, giving a reliable animation instead of
   * depending on CSS grid auto-sizing.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');

    if (!expanded) {
      // About to open — measure the real content height so the CSS transition
      // has a concrete pixel target to animate towards.
      content.style.setProperty('--disclosure-height', `${content.scrollHeight}px`);
    }

    trigger.setAttribute('aria-expanded', String(!expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );
    content.inert = expanded;
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}